import React from "react";
import { FeatherInfo } from "@subframe/core";
import { getEffectiveTotalFromRawJson } from "../../utils/review/invoiceBillEffectiveTotal";
import {
  formatReverseChargeAmountWithDocument,
  getReverseChargeContextFromJson,
  resolveReverseChargeAmount,
} from "../../utils/reverseCharge";

export type InvoiceBillFinancialTotalsProps = {
  displayJson: Record<string, unknown>;
  moduleKind: "invoice" | "bill";
  currencySymbol: string;
  resolvedGstTreatment?: string;
  resolvedGstCategory?: string;
  formatDate?: (value: string | undefined) => string;
};

export const InvoiceBillFinancialTotals: React.FC<
  InvoiceBillFinancialTotalsProps
> = ({
  displayJson,
  moduleKind,
  currencySymbol,
  resolvedGstTreatment,
  resolvedGstCategory,
  formatDate,
}) => {
  const rcContext = getReverseChargeContextFromJson(
    displayJson,
    resolvedGstTreatment,
    resolvedGstCategory,
  );

  const formatMoney = (amount: number) =>
    amount.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const tcsPct =
    displayJson?.tcs_tax_id &&
    displayJson.tcs_tax_id !== "" &&
    Number(displayJson.tcs_tax_percentage) > 0
      ? Number(displayJson.tcs_tax_percentage)
      : 0;

  const tcsBase = rcContext.showMessage
    ? Number(displayJson.sub_total) || 0
    : (Number(displayJson.sub_total) || 0) +
      (Number(displayJson.tax_total) || 0);
  const tcsAmount = tcsPct > 0 ? (tcsBase * tcsPct) / 100 : 0;

  const displayTotal =
    tcsPct > 0
      ? getEffectiveTotalFromRawJson(
          displayJson,
          moduleKind,
          resolvedGstTreatment,
          resolvedGstCategory,
        )
      : rcContext.showMessage
        ? getEffectiveTotalFromRawJson(
            displayJson,
            moduleKind,
            resolvedGstTreatment,
            resolvedGstCategory,
          )
        : Number(displayJson.total ?? 0);

  return (
    <>
      {displayJson?.due_date != null && displayJson?.due_date !== "" && (
        <div className="flex w-full items-center justify-between">
          <span className="text-body font-body text-subtext-color">
            Due Date
          </span>
          <span className="text-body font-body text-default-font">
            {formatDate
              ? formatDate(String(displayJson.due_date))
              : String(displayJson.due_date)}
          </span>
        </div>
      )}
      {(displayJson?.sub_total != null ||
        Number(displayJson?.sub_total) === 0 ||
        displayJson?.total != null) && (
        <div className="flex w-full items-center justify-between">
          <span className="text-body font-body text-subtext-color">
            Subtotal
          </span>
          <span className="text-body font-body text-default-font">
            {currencySymbol} {formatMoney(Number(displayJson?.sub_total ?? 0))}
          </span>
        </div>
      )}
      {displayJson?.tax_total != null && displayJson?.tax_total !== 0 && (
        <div className="flex w-full items-center justify-between">
          <span className="text-body font-body text-subtext-color">
            Tax Total
          </span>
          <span className="text-body font-body text-default-font">
            {currencySymbol}{" "}
            {formatMoney(Number(displayJson?.tax_total ?? 0))}
          </span>
        </div>
      )}
      {tcsPct > 0 && (
        <div className="flex w-full items-center justify-between">
          <span className="text-body font-body text-subtext-color">
            {displayJson?.tcs_tax_name
              ? `TCS Total (${displayJson.tcs_tax_name})`
              : "TCS Total"}
          </span>
          <span className="text-body font-body text-default-font">
            {currencySymbol} {formatMoney(tcsAmount)}
          </span>
        </div>
      )}
      {displayJson?.discount != null &&
        Number(displayJson.discount) !== 0 && (
        <div className="flex w-full items-center justify-between">
          <span className="text-body font-body text-subtext-color">
            Discount
          </span>
          <span className="text-body font-body text-default-font">
            {currencySymbol}{" "}
            {formatMoney(Number(displayJson?.discount ?? 0))}
          </span>
        </div>
      )}
      {(displayJson?.total != null || Number(displayJson?.total) === 0) && (
        <div className="flex w-full items-center justify-between">
          <span className="text-body font-body text-subtext-color">Total</span>
          <span className="text-body font-body text-default-font">
            {currencySymbol} {formatMoney(displayTotal)}
          </span>
        </div>
      )}
      {rcContext.showMessage && (
        <div className="flex w-full items-start gap-2 rounded-md border border-solid border-info-200 bg-info-50 px-3 py-2">
          <FeatherInfo className="h-4 w-4 shrink-0 text-info-700" />
          <span className="text-body font-body text-info-700">
            Reverse charges of{" "}
            {formatReverseChargeAmountWithDocument(
              resolveReverseChargeAmount({
                taxTotal: rcContext.taxTotal,
                documentCurrencyCode: displayJson?.currency_code as
                  | string
                  | undefined,
                documentCurrencySymbol: currencySymbol,
                exchangeRate: displayJson?.exchange_rate as
                  | number
                  | undefined,
              }),
            )}{" "}
            will be applied
          </span>
        </div>
      )}
    </>
  );
};
